/** @format */

import {
  selectorBookingAvailability,
  selectorBookingCheckInDate,
  selectorBookingCheckOutDate,
  selectorBookingCommercialConditions,
  selectorBookingContactDetails,
  selectorBookingContractualConditions,
  selectorBookingCurrentStep,
  selectorBookingError,
  selectorBookingFinancialValidation,
  selectorBookingFormId,
  selectorBookingLoaders,
  selectorBookingOptions,
  selectorBookingPaymentInfo,
  selectorBookingPersonalDetails,
  selectorBookingResidentialAddress,
  selectorBookingRoom,
  selectorBookingSecondOccupant,
  selectorBookingValidCheckOutDates,
  selectorHowDidYouFindUs,
} from '@/ducks/bookingNew/selectors';
import {
  BookingContactDetails,
  BookingFinancialValidation,
  BookingPersonalDetails,
  BookingResidentialAddress,
  BookingSecondOccupant,
  BookingStep,
} from '@/ducks/bookingNew/types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

export const useBookingStore = () => {
  const dispatch = useAppDispatch();

  const options = useAppSelector(selectorBookingOptions);
  const loaders = useAppSelector(selectorBookingLoaders);
  const room = useAppSelector(selectorBookingRoom);
  const availability = useAppSelector(selectorBookingAvailability);
  const checkInDate = useAppSelector(selectorBookingCheckInDate);
  const checkOutDate = useAppSelector(selectorBookingCheckOutDate);
  const contactDetails = useAppSelector(selectorBookingContactDetails);
  const personalDetails = useAppSelector(selectorBookingPersonalDetails);
  const secondOccupant = useAppSelector(selectorBookingSecondOccupant);
  const residentialAddress = useAppSelector(selectorBookingResidentialAddress);
  const validCheckOutDates = useAppSelector(selectorBookingValidCheckOutDates);
  const contractualConditions = useAppSelector(selectorBookingContractualConditions);
  const commercialConditions = useAppSelector(selectorBookingCommercialConditions);
  const financialValidation = useAppSelector(selectorBookingFinancialValidation);
  const errorState = useAppSelector(selectorBookingError);
  const step = useAppSelector(selectorBookingCurrentStep);
  const howDidYouFindUs = useAppSelector(selectorHowDidYouFindUs);
  const paymentInfo = useAppSelector(selectorBookingPaymentInfo);
  const bookingFormId = useAppSelector(selectorBookingFormId);

  const setContactDetails = (formData: BookingContactDetails) => {
    dispatch({
      type: 'BOOKING_SET_CONTACT_DETAILS',
      payload: formData,
    });
  };

  const setContactDetailsFormError = (error: boolean) => {
    dispatch({
      type: 'BOOKING_LIGHT_IDENTITY_FORM_FAILURE',
      payload: error,
    });
  };

  const setPersonalDetails = (formData: BookingPersonalDetails) => {
    dispatch({
      type: 'BOOKING_SET_PERSONAL_DETAILS',
      payload: formData,
    });
  };

  const setPersonalDetailsFormError = (error: boolean) => {
    dispatch({
      type: 'BOOKING_SET_PERSONAL_DETAILS_FORM_ERROR',
      payload: error,
    });
  };

  const setPersonalDetailsError = (error: boolean) => {
    dispatch({
      type: 'BOOKING_SET_PERSONAL_DETAILS_ERROR',
      payload: error,
    });
  };

  const setSecondOccupant = (formData: BookingSecondOccupant) => {
    dispatch({
      type: 'BOOKING_SET_SECOND_OCCUPANT',
      payload: formData,
    });
  };

  const setSecondOccupantFormError = (error: boolean) => {
    dispatch({
      type: 'BOOKING_SET_SECOND_OCCUPANT_FORM_ERROR',
      payload: error,
    });
  };

  const setSecondOccupantError = (error: boolean) => {
    dispatch({
      type: 'BOOKING_SET_SECOND_OCCUPANT_ERROR',
      payload: error,
    });
  };

  const setResidentialAddress = (formData: BookingResidentialAddress) => {
    dispatch({
      type: 'BOOKING_SET_RESIDENTIAL_ADDRESS',
      payload: formData,
    });
  };

  const setResidentialAddressFormError = (error: boolean) => {
    dispatch({
      type: 'BOOKING_SET_RESIDENTIAL_ADDRESS_FORM_ERROR',
      payload: error,
    });
  };

  const setResidentialAddressError = (error: boolean) => {
    dispatch({
      type: 'BOOKING_SET_RESIDENTIAL_ADDRESS_ERROR',
      payload: error,
    });
  };

  const setCheckInDate = (checkInDate: string) => {
    dispatch({
      type: 'BOOKING_SET_CHECK_IN_DATE',
      payload: checkInDate,
    });
  };

  const setCheckOutDate = (checkOutDate: string) => {
    dispatch({
      type: 'BOOKING_SET_CHECK_OUT_DATE',
      payload: checkOutDate,
    });
  };

  const setFinancialValidation = (formData: BookingFinancialValidation) => {
    dispatch({
      type: 'BOOKING_SET_FINANCIAL_VALIDATION',
      payload: formData,
    });
  };

  const setFinancialValidationFormError = (error: boolean) => {
    dispatch({
      type: 'BOOKING_SET_FINANCIAL_VALIDATION_FORM_ERROR',
      payload: error,
    });
  };

  const setFinancialValidationError = (error: boolean) => {
    dispatch({
      type: 'BOOKING_SET_FINANCIAL_VALIDATION_ERROR',
      payload: error,
    });
  };

  const setStep = (step: BookingStep) => {
    dispatch({ type: 'BOOKING_SET_STEP', payload: step });
  };

  const setHowDidYouFindUs = (howDidYouFindUs: string) => {
    dispatch({ type: 'BOOKING_SET_HOW_FIND_US', payload: howDidYouFindUs });
  };

  const setHowDidYouFindUsFormErrors = (error: boolean) => {
    dispatch({ type: 'BOOKING_SET_HOW_FIND_US_FORM_ERROR', payload: error });
  };

  const activatePaymentStep = () => {
    dispatch({ type: 'BOOKING_ACTIVATE_PAYMENT_STEP' });
  };

  const resetValidCheckoutDates = () => {
    dispatch({ type: 'BOOKING_RESET_VALID_CHECKOUT_DATES' });
  };

  const addLoader = (id: string) => {
    dispatch({
      type: 'BOOKING_ADD_LOADER',
      payload: id,
    });
  };

  const removeLoader = (id: string) => {
    dispatch({
      type: 'BOOKING_REMOVE_LOADER',
      payload: id,
    });
  };

  return {
    options,
    loading: !!loaders.length,
    loaders,
    addLoader,
    removeLoader,
    room,
    availability,
    contactDetails,
    setContactDetails,
    setContactDetailsFormError,
    personalDetails,
    setPersonalDetails,
    setPersonalDetailsFormError,
    setPersonalDetailsError,
    secondOccupant,
    setSecondOccupant,
    setSecondOccupantFormError,
    setSecondOccupantError,
    residentialAddress,
    setResidentialAddress,
    setResidentialAddressFormError,
    setResidentialAddressError,
    checkInDate,
    setCheckInDate,
    checkOutDate,
    setCheckOutDate,
    resetValidCheckoutDates,
    validCheckOutDates,
    contractualConditions,
    commercialConditions,
    activatePaymentStep,
    financialValidation,
    setFinancialValidation,
    setFinancialValidationFormError,
    setFinancialValidationError,
    errorState,
    step,
    setCurrentStep: setStep,
    howDidYouFindUs,
    setHowDidYouFindUs,
    setHowDidYouFindUsFormErrors,
    paymentInfo,
    bookingFormId,
  };
};
